import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useBulkSetPaymentTermsMutation } from "../../store/apis/companyApi";

interface BulkPaymentTermsModalProps {
  open: boolean;
  companyIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
}

const BulkPaymentTermsModal: React.FC<BulkPaymentTermsModalProps> = ({
  open,
  companyIds,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [bulkSetPaymentTerms, { isLoading: isSubmitting }] =
    useBulkSetPaymentTermsMutation();
  const [value, setValue] = useState("");

  const handleClose = () => {
    setValue("");
    onClose();
  };

  const handleConfirm = async () => {
    try {
      const trimmed = value.trim();
      const response = await bulkSetPaymentTerms({
        companyIds,
        paymentTerms: trimmed === "" ? null : trimmed,
      }).unwrap();
      dispatch(
        setToastMessage({
          message: t("company.bulkPaymentTerms.success", {
            updatedCount: response.data.updatedCount,
          }),
          severity: "success",
        })
      );
      setValue("");
      onSuccess?.();
      onClose();
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        t("company.bulkPaymentTerms.error");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-payment-terms-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="bulk-payment-terms-title" sx={{ pb: 2, px: 3 }}>
        <Typography variant="h5">
          {t("company.bulkPaymentTerms.title")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            select
            label={t("company.bulkPaymentTerms.inputLabel")}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={isSubmitting}
          >
            <MenuItem value="">
              {t("company.paymentTermsUndefined")}
            </MenuItem>
            <MenuItem value="30">30</MenuItem>
            <MenuItem value="45">45</MenuItem>
            <MenuItem value="60">60</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ color: (theme) => theme.palette.neutral[600] }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          sx={{ color: "white" }}
        >
          {t("common.confirm", "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkPaymentTermsModal;
